import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Briefcase, Award, Globe, Users } from 'lucide-react';
import AnimatedSection from '../components/AnimatedSection';

const About = () => {
  const { language } = useLanguage();

  const content = {
    en: {
      title: "Who are we?",
      subtitle: "Bridging British Excellence with Italian Operations",
      intro: "With over 15 years of international experience as a Lead Auditor for ISO certification, I specialize in helping organizations achieve and maintain ISO 9001, 14001, 45001, and 27001 certifications while navigating the complexities of cross-border business operations between the UK and Italy.",
      
      experience: {
        title: "Professional Background",
        summary: "Throughout my career, I have built extensive expertise working across Italy and the United Kingdom, specializing in ISO certification auditing and Health, Safety & Environment (HSE) compliance. My experience spans multiple regions including London, Milan, La Spezia, Rome, and The Midlands, where I have worked with diverse organizations ranging from multinational corporations to local Italian businesses.",
        keyAreas: [
          {
            icon: "award",
            title: "Lead Auditor & Certification Expert",
            location: "Italy & UK",
            duration: "15+ years",
            description: "Extensive experience conducting certification audits for ISO 9001, ISO 14001, ISO 45001:2018, and ISO 27001 standards. Specialized in construction site inspections, safety management system assessments, cybersecurity compliance, and verification across both Italian and UK markets."
          },
          {
            icon: "briefcase",
            title: "Operations & Business Development",
            location: "London, Milan & La Spezia",
            duration: "10+ years",
            description: "Led international operations, business development initiatives, and performance improvement programs. Managed safety management systems, auditing processes, and client relationships across Europe, with particular focus on UK-Italy cross-border operations."
          },
          {
            icon: "globe",
            title: "Cross-Border Compliance Management",
            location: "UK & Italy",
            duration: "10+ years",
            description: "Managed international compliance operations bridging UK and Italian markets. Specialized in helping UK organizations establish Italian operations and supporting Italian companies in meeting international standards and serving English-speaking markets."
          }
        ]
      },
      
      expertise: {
        title: "Unique Cross-Border Expertise",
        points: [
          {
            icon: "globe",
            title: "London → Italy",
            text: "Supporting UK businesses expanding operations into Italy, navigating Italian compliance requirements and business culture"
          },
          {
            icon: "users",
            title: "Italy → UK/International",
            text: "Helping Italian companies (La Spezia, Milan, Rome) serve English-speaking clients and meet international standards"
          },
          {
            icon: "award",
            title: "ISO & HSE Specialist",
            text: "Expert in ISO 9001, 14001, 45001, 27001 certifications, cybersecurity compliance, and workplace Health, Safety & Environment standards"
          },
          {
            icon: "briefcase",
            title: "Key Locations",
            text: "Operating across La Spezia, Milan, Rome, London, and throughout Italy and the UK"
          }
        ]
      },
      
      approach: {
        title: "Why Work With Us",
        description: "As bilingual professionals with deep roots in both London and Italy, we understand the nuances of conducting business across these markets. Our extensive international experience has given us unique insight into what organizations need when working across borders:",
        benefits: [
          "Fluent communication in English and Italian",
          "Deep understanding of both British and Italian business culture",
          "Proven expertise with international and multinational organizations",
          "Local knowledge in La Spezia, Milan, Rome, and London",
          "15+ years of hands-on ISO auditing and certification experience",
          "Practical, results-oriented approach to compliance"
        ]
      },
      
      cta: "Whether you're a London-based company expanding to Italy, or an Italian business serving international clients, we can help you achieve compliance while respecting the unique requirements of each market."
    },
    
    it: {
      title: "Chi Sono",
      subtitle: "Un Ponte tra l'Eccellenza Britannica e le Operazioni Italiane",
      intro: "Con oltre 15 anni di esperienza internazionale come Lead Auditor per certificazioni ISO, sono specializzata nell'aiutare le organizzazioni a ottenere e mantenere le certificazioni ISO 9001, 14001, 45001 e 27001, gestendo le complessità delle operazioni commerciali transfrontaliere tra Regno Unito e Italia.",
      
      experience: {
        title: "Background Professionale",
        summary: "Nel corso della mia carriera, ho sviluppato un'ampia competenza lavorando in Italia e nel Regno Unito, specializzandomi in audit di certificazione ISO e conformità HSE (Salute, Sicurezza e Ambiente). La mia esperienza si estende su diverse regioni tra cui Londra, Milano, La Spezia, Roma e The Midlands, dove ho collaborato con organizzazioni diverse, dalle multinazionali alle imprese italiane locali.",
        keyAreas: [
          {
            icon: "award",
            title: "Lead Auditor ed Esperta di Certificazione",
            location: "Italia e UK",
            duration: "15+ anni",
            description: "Ampia esperienza nella conduzione di audit di certificazione per gli standard ISO 9001, ISO 14001, ISO 45001:2018 e ISO 27001. Specializzata in ispezioni di cantieri, valutazioni di sistemi di gestione della sicurezza, conformità cybersecurity e verifica della conformità nei mercati italiano e britannico."
          },
          {
            icon: "briefcase",
            title: "Operazioni e Sviluppo Commerciale",
            location: "Londra, Milano e La Spezia",
            duration: "10+ anni",
            description: "Ho guidato operazioni internazionali, iniziative di sviluppo commerciale e programmi di miglioramento delle prestazioni. Gestito sistemi di gestione della sicurezza, processi di audit e relazioni con clienti in tutta Europa, con particolare attenzione alle operazioni transfrontaliere UK-Italia."
          },
          {
            icon: "globe",
            title: "Gestione Conformità Transfrontaliera",
            location: "UK e Italia",
            duration: "10+ anni",
            description: "Gestito operazioni di conformità internazionale tra i mercati britannico e italiano. Specializzata nell'aiutare organizzazioni britanniche a stabilire operazioni italiane e nel supportare aziende italiane nel rispettare standard internazionali e servire mercati anglofoni."
          }
        ]
      },
      
      expertise: {
        title: "Competenza Unica Transfrontaliera",
        points: [
          {
            icon: "globe",
            title: "Londra → Italia",
            text: "Supporto aziende britanniche nell'espansione in Italia, gestendo requisiti di conformità italiana e cultura aziendale"
          },
          {
            icon: "users",
            title: "Italia → UK/Internazionale",
            text: "Aiuto aziende italiane (La Spezia, Milano, Roma) a servire clienti anglofoni e rispettare standard internazionali"
          },
          {
            icon: "award",
            title: "Specialista ISO & HSE",
            text: "Esperta in certificazioni ISO 9001, 14001, 45001, 27001, conformità cybersecurity e standard Salute, Sicurezza e Ambiente sul lavoro"
          },
          {
            icon: "briefcase",
            title: "Località Principali",
            text: "Operativa a La Spezia, Milano, Roma, Londra e in tutta Italia e Regno Unito"
          }
        ]
      },
      
      approach: {
        title: "Perché Lavorare Con Noi",
        description: "Come professionisti bilingui con radici profonde sia a Londra che in Italia, comprendiamo le sfumature del fare affari in questi mercati. La nostra ampia esperienza internazionale ci ha dato una visione unica di ciò che le organizzazioni necessitano quando operano oltre confine:",
        benefits: [
          "Comunicazione fluente in inglese e italiano",
          "Profonda comprensione della cultura aziendale britannica e italiana",
          "Competenza comprovata con organizzazioni internazionali e multinazionali",
          "Conoscenza locale di La Spezia, Milano, Roma e Londra",
          "15+ anni di esperienza pratica in audit e certificazione ISO",
          "Approccio pratico e orientato ai risultati alla conformità"
        ]
      },
      
      cta: "Che tu sia un'azienda londinese in espansione in Italia, o un'impresa italiana che serve clienti internazionali, possiamo aiutarti a raggiungere la conformità rispettando i requisiti unici di ciascun mercato."
    }
  };

  const t = content[language];

  const getIcon = (iconName) => {
    const icons = {
      globe: <Globe size={32} />,
      users: <Users size={32} />,
      award: <Award size={32} />,
      briefcase: <Briefcase size={32} />
    };
    return icons[iconName];
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <AnimatedSection>
          <div className="about-profile-photo">
            <img 
              src="https://customer-assets-lqy194kg.emergentagent.net/job_iso-compliance-hub-8/artifacts/yzyq090v_Screenshot_20260822_145826_Gallery.jpg" 
              alt="Lesley Cefaliello - Founder and Senior Consultant"
              className="profile-image"
            />
          </div>
          <div className="about-profile-info">
            <h2 className="profile-name">Lesley Cefaliello</h2>
            <div className="profile-title-with-logo">
              <p className="profile-title">Founder and Senior Consultant</p>
              <img 
                src="https://customer-assets-lqy194kg.emergentagent.net/job_iso-compliance-hub-8/artifacts/in3c33x5_1000035409.png" 
                alt="MCG"
                className="about-clean-logo"
              />
            </div>
          </div>
          <h1 className="about-title">{t.title}</h1>
          <p className="about-subtitle">{t.subtitle}</p>
          <p className="about-intro">{t.intro}</p>
        </AnimatedSection>
      </section>

      {/* Experience Section */}
      <section className="about-experience">
        <AnimatedSection>
          <h2 className="section-title">{t.experience.title}</h2>
          <p className="experience-summary">{t.experience.summary}</p>
          <div className="experience-timeline">
            {t.experience.keyAreas.map((area, idx) => (
              <AnimatedSection key={idx} delay={idx * 0.15} className="experience-card">
                <div className="experience-role">{area.title}</div>
                <div className="experience-meta">
                  <span className="experience-duration">{area.duration}</span>
                  <span className="experience-location">{area.location}</span>
                </div>
                <p className="experience-description">{area.description}</p>
              </AnimatedSection>
            ))}
          </div>
        </AnimatedSection>
      </section>

      {/* Expertise Section */}
      <section className="about-expertise">
        <AnimatedSection>
          <h2 className="section-title">{t.expertise.title}</h2>
        </AnimatedSection>
        <div className="expertise-grid">
          {t.expertise.points.map((point, idx) => (
            <AnimatedSection key={idx} delay={idx * 0.1} className="expertise-card">
              <div className="expertise-icon">{getIcon(point.icon)}</div>
              <h3 className="expertise-title">{point.title}</h3>
              <p className="expertise-text">{point.text}</p>
            </AnimatedSection>
          ))}
        </div>
      </section>

      {/* Approach Section */}
      <section className="about-approach">
        <AnimatedSection>
          <h2 className="section-title">{t.approach.title}</h2>
          <p className="approach-description">{t.approach.description}</p>
          <ul className="approach-benefits">
            {t.approach.benefits.map((benefit, idx) => (
              <li key={idx}>{benefit}</li>
            ))}
          </ul>
        </AnimatedSection>
      </section>

      {/* CTA Section */}
      <section className="about-cta">
        <AnimatedSection>
          <p className="cta-text">{t.cta}</p>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default About;
